import { datadogRum } from '@datadog/browser-rum';
import { AuthChangeEvent, Session } from '@supabase/supabase-js';

import { User } from 'oidc-client-ts';
import { ReactNode, useEffect, useState } from 'react';
import { v5 as uuidv5 } from 'uuid';

import { HAS_SIGNED_IN_KEY } from '@/constants/authConstants';
import { backendAuthType, mixedLoginMethods } from '@/utils/constants';

import {
  basicManager,
  oauthManager,
  oauthProviderSpecificManager,
  supabase,
} from '../../utils/backend-auth';
import { AuthContext } from './AuthContext';
import { SupabaseAuthStateChangeContext } from './SupabaseAuthStateChangeContext';

const NAMESPACE_OID = '6ba7b812-9dad-11d1-80b4-00c04fd430c8';
export type AuthSession = {
  user: {
    id: string;
    email: string;
  };
};

function supabaseSessionToAuthSession(
  session: Session | null
): AuthSession | null {
  if (!session) return null;
  return {
    user: {
      id: session.user.id,
      email: session.user.email || '',
    },
  };
}
function oauthUserToAuthSession(user: User | null): AuthSession | null {
  if (!user) return null;
  const uuid = uuidv5(user.profile.email || '', NAMESPACE_OID);
  return {
    user: {
      id: uuid || '',
      email: user.profile.email || '',
    },
  };
}

function oauthGoogleUserToAuthSession(
  userInfo: string | null
): AuthSession | null {
  if (!userInfo) return null;
  const user = JSON.parse(atob(userInfo));

  return {
    user: {
      id: user.userId,
      email: user.email || '',
    },
  };
}

export type AuthSessionState = AuthSession | null | undefined;

/**
 * useAuth can be invoked anywhere and other sections of the app
 * require auth state to be stable between renders (ie. feature flags)
 */
export function GlobalAuthProvider(props: { children: ReactNode }) {
  const needsAuth =
    backendAuthType === 'supabase' ||
    backendAuthType === 'oauth' ||
    backendAuthType === 'mixed';

  function handleUserLoaded(user: User | null) {
    oauthManager?.storeUser(user);
    setSession(oauthUserToAuthSession(user));
  }
  function handleUserSignedIn() {
    oauthManager?.getUser().then((user) => {
      if (user) localStorage.setItem(HAS_SIGNED_IN_KEY, 'true');
      setSession(oauthUserToAuthSession(user));
    });
  }
  function handleUserSignedOut() {
    oauthManager?.removeUser();
    setSession(null);
  }
  function handleUserUnloaded() {
    setSession(null);
  }
  function handleSilentRenewError() {
    oauthManager?.removeUser();
    setSession(null);
  }

  const ctxValue = useState<AuthSession | null | undefined>(undefined);
  const [, setSession] = ctxValue;

  useEffect(() => {
    if (!needsAuth) {
      setSession(null);
      return;
    }

    if (mixedLoginMethods.basic) {
      if (!basicManager) {
        throw new Error(
          `Expected basic manager to be defined for auth type ${backendAuthType}`
        );
      }

      return basicManager.register(setSession);
    }

    if (backendAuthType === 'supabase') {
      if (!supabase) {
        throw new Error(
          `Expected supabase client to be defined for auth type ${backendAuthType}`
        );
      }
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) localStorage.setItem(HAS_SIGNED_IN_KEY, 'true');
        setSession(supabaseSessionToAuthSession(session));

        datadogRum.setUser({
          id: session?.user.id,
          email: session?.user.email,
        });
      });

      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((_event, session) => {
        if (session) localStorage.setItem(HAS_SIGNED_IN_KEY, 'true');
        setSession(supabaseSessionToAuthSession(session));

        datadogRum.setUser({
          id: session?.user.id,
          email: session?.user.email,
        });
      });
      return () => subscription.unsubscribe();
    }

    if (mixedLoginMethods.customOidc) {
      if (!oauthProviderSpecificManager) {
        throw new Error(
          `Expected oauth provider specific manager to be defined for auth type ${backendAuthType} with google login enabled`
        );
      } else {
        const userInfo = oauthProviderSpecificManager.getUserInfo();
        if (!userInfo) {
          setSession(null);
        } else {
          localStorage.setItem(HAS_SIGNED_IN_KEY, 'true');
          setSession(oauthGoogleUserToAuthSession(userInfo));
        }
        return oauthProviderSpecificManager.register(setSession);
      }
    }

    if (backendAuthType === 'oauth') {
      if (!oauthManager) {
        throw new Error(
          `Expected oauth manager to be defined for auth type ${backendAuthType}`
        );
      } else {
        oauthManager.getUser().then((user) => {
          if (!user) {
            setSession(null);
          } else {
            if (user.expired) {
              oauthManager
                ?.signinSilent()
                .then((refreshedUser) => {
                  // Handle failed silent renew
                  if (!refreshedUser) {
                    oauthManager?.removeUser();
                    setSession(null);
                  } else {
                    setSession(oauthUserToAuthSession(refreshedUser));
                  }
                })
                .catch(() => {
                  oauthManager?.removeUser();
                  setSession(null);
                });
            } else {
              setSession(oauthUserToAuthSession(user));
            }
          }
        });
        oauthManager.events.addUserLoaded(handleUserLoaded);
        oauthManager.events.addUserSignedIn(handleUserSignedIn);
        oauthManager.events.addUserSignedOut(handleUserSignedOut);
        oauthManager.events.addUserUnloaded(handleUserUnloaded);
        oauthManager.events.addSilentRenewError(handleSilentRenewError);
        return () => {
          oauthManager?.events.removeUserLoaded(handleUserLoaded);
          oauthManager?.events.removeUserSignedIn(handleUserSignedIn);
          oauthManager?.events.removeUserSignedOut(handleUserSignedOut);
          oauthManager?.events.removeUserUnloaded(handleUserUnloaded);
          oauthManager?.events.removeSilentRenewError(handleSilentRenewError);
        };
      }
    }
  }, [needsAuth]);

  return (
    <AuthContext.Provider value={ctxValue}>
      {props.children}
    </AuthContext.Provider>
  );
}

// This exists for any supabase authStateChange events that are fired
// on page load. For some reason on Safari, the authStateChange event
// sometimes gets fired before components render thus the event callbacks are not
// registered at the time events are fired. This provider subscribes to events and
// stores the last event fired so that the component can consume it on render.
// Right now only used for PASSWORD_RECOVERY and SIGN_UP events.
export function SupabaseAuthStateChangeProvider(props: {
  children: ReactNode;
}) {
  const [lastEvent, setLastEvent] = useState<AuthChangeEvent | undefined>(
    undefined
  );

  useEffect(() => {
    if (backendAuthType === 'supabase' && supabase) {
      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((_event) => {
        setLastEvent(_event);
      });
      return () => subscription.unsubscribe();
    }
  }, []);

  return (
    <SupabaseAuthStateChangeContext.Provider value={[lastEvent, setLastEvent]}>
      {props.children}
    </SupabaseAuthStateChangeContext.Provider>
  );
}
